import React from 'react';
import { Control, Controller, UseFormWatch } from 'react-hook-form';

import {
  AppButton,
  AppForm,
  AppFormLabel,
  AppSelect,
  AppTextField,
  AppTypography,
} from '@/components/atoms';
import { RA2NDatePicker } from '@/components/organisms';
import { usePatientMasterData } from '@/hooks/main/usePatientMasterData';

import { NO_MEDICAL_AID_ID } from './constants';
import type { TPatientCertificationForm } from './schema';

interface MedicalAidSectionProps {
  control: Control<TPatientCertificationForm>;
  watch: UseFormWatch<TPatientCertificationForm>;
}

/**
 * 公費負担医療情報 section.
 *
 * Display rules:
 * - Legacy A2/RA2 certification screen renders medical aid controls as disabled.
 * - Existing medical aid data is handled on the patient edit screen, not edited here.
 */
export const MedicalAidSection: React.FC<MedicalAidSectionProps> = ({ control, watch }) => {
  const { data: masterData } = usePatientMasterData();
  const insurerTypeId = watch('medical_aid.insurer_type_id');
  const isNoMedicalAid = insurerTypeId == null || insurerTypeId === NO_MEDICAL_AID_ID;
  const insurerTypes = (
    masterData?.insurer_types?.length
      ? masterData.insurer_types
      : [{ id: NO_MEDICAL_AID_ID, name: 'なし' }]
  ) as Array<{ id: number; name: string }>;

  return (
    <>
      <Controller
        name="medical_aid.medical_aid_number"
        control={control}
        render={({ field: numberField, fieldState: { error: numberError } }) => (
          <AppForm.Item
            label={<AppFormLabel label="公費負担者番号" />}
            validateStatus={numberError ? 'error' : undefined}
            help={numberError?.message}
          >
            <div className="flex flex-col w-full gap-2 sm:flex-row sm:flex-wrap sm:items-center">
              <Controller
                name="medical_aid.insurer_type_id"
                control={control}
                render={({ field: typeField, fieldState: { error: typeError } }) => (
                  <AppSelect
                    {...typeField}
                    className="!w-[197px]"
                    disabled
                    placeholder="医療助成種別"
                    status={typeError ? 'error' : undefined}
                    options={insurerTypes.map(t => ({ label: t.name, value: t.id }))}
                  />
                )}
              />
              <AppTextField
                {...numberField}
                value={numberField.value ?? ''}
                status={numberError ? 'error' : undefined}
                className="w-full max-w-[150px]"
                placeholder="公費負担者番号"
                disabled
              />
              <AppButton type="primary" className="w-full bg-[#33b5e5] sm:w-auto" disabled>
                補助入力
              </AppButton>
              <AppFormLabel label="公費受給者番号" className="text-gray-700" />
              <Controller
                name="medical_aid.medical_aid_patient_number"
                control={control}
                render={({ field: patientField, fieldState: { error: patientError } }) => (
                  <AppTextField
                    {...patientField}
                    value={patientField.value ?? ''}
                    status={patientError ? 'error' : undefined}
                    className="w-full max-w-[150px]"
                    placeholder="公費受給者番号"
                    disabled
                  />
                )}
              />
            </div>
          </AppForm.Item>
        )}
      />

      {isNoMedicalAid && (
        <AppForm.Item label={<AppFormLabel label=" " />}>
          <AppTypography.Text className="text-[#ff5b57]">
            自己負担額に上限がある場合は、補助入力から選択してください。
          </AppTypography.Text>
        </AppForm.Item>
      )}

      <Controller
        name="medical_aid.application_start_date"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <AppForm.Item
            label={<AppFormLabel label="適用開始日" />}
            validateStatus={error ? 'error' : undefined}
            help={error?.message}
          >
            <RA2NDatePicker
              value={field.value ?? ''}
              onChange={field.onChange}
              className="w-full sm:w-[197px]"
              placeholder=""
              disabled
            />
          </AppForm.Item>
        )}
      />

      <Controller
        name="medical_aid.application_end_date"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <AppForm.Item
            label={<AppFormLabel label="適用終了日" />}
            validateStatus={error ? 'error' : undefined}
            help={error?.message}
          >
            <RA2NDatePicker
              value={field.value ?? ''}
              onChange={field.onChange}
              className="w-full sm:w-[197px]"
              placeholder=""
              disabled
            />
          </AppForm.Item>
        )}
      />
    </>
  );
};
